import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  InputLabel,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useHistory, useLocation } from "react-router";

import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
  PermissionTooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { OverageField } from "components/settings/shared/overage/overage-field";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  cancelFutureSubscription,
  scheduleCancellation,
  undoScheduledCancellation,
} from "data/mutations-common";
import {
  fetchCredits,
  fetchPaymentMethod,
  fetchSubscription,
} from "data/queries-common";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { toTitleCase } from "utils/commons";
import { formatInterval, formatPrice } from "utils/format";
import { resolveIdentityId } from "utils/identity";
import CreditUseModal from "./credit-use-modal";
import FeatureComparisonModal from "./feature-comparison-modal";

export const Subscription = () => {
  const history = useHistory();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const [featureComparisonModalOpen, setFeatureComparisonModalOpen] =
    useState<boolean>(false);
  const [creditUseModalOpen, setCreditUseModalOpen] = useState<boolean>(false);

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  const location = useLocation();
  const currentRoute = location.pathname;

  useEffect(() => {
    const routeActions: Record<string, () => void> = {
      [Routes.orgFeatureComparisonTable]: () =>
        setFeatureComparisonModalOpen(true),
      [Routes.orgFeatureCostTable]: () => setCreditUseModalOpen(true),
    };

    const action = routeActions[currentRoute];
    if (action) action();
  }, [currentRoute]);

  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
    enabled: !!subscriptionQuery.data?.activeSubscription,
  });

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const futureSubscriptionMutation = useMutation({
    mutationFn: () => cancelFutureSubscription(),
    onSuccess: () => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscriptionQuery.data,
        activeSubscription: {
          ...subscriptionQuery.data?.activeSubscription,
          nextSub: undefined,
        },
      });
      setCancelPlanChangeModalIsOpen(false);
      enqueueSnackbar(
        "You successfully cancelled your pending subscription downgrade.",
        {
          variant: "success",
        },
      );
    },
  });

  const scheduleCancellationMutation = useMutation({
    mutationFn: () => scheduleCancellation(),
    onSuccess: () => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscriptionQuery.data,
        activeSubscription: {
          ...subscriptionQuery.data?.activeSubscription,
          cancelAtPeriodEnd: true,
        },
      });
      setCancelModalIsOpen(false);
      enqueueSnackbar(
        "You successfully scheduled a subscription cancellation.",
        {
          variant: "success",
        },
      );
    },
  });

  const undoScheduledCancellationMutation = useMutation({
    mutationFn: () => undoScheduledCancellation(),
    onSuccess: () => {
      queryClient.setQueryData([QueryKeys.subscription, identityId], {
        ...subscriptionQuery.data,
        activeSubscription: {
          ...subscriptionQuery.data?.activeSubscription,
          cancelAtPeriodEnd: false,
        },
      });
      setUndoCancelModalIsOpen(false);
      enqueueSnackbar(
        "You successfully cancelled your pending subscription cancellation.",
        {
          variant: "success",
        },
      );
    },
  });

  const hasScheduledUpgradeOrDowngrade = subscriptionQuery.data
    ? !!subscriptionQuery.data.activeSubscription &&
      subscriptionQuery.data.activeSubscription.nextSub != null
    : true;

  const hasScheduledCancellation = subscriptionQuery.data
    ? !!subscriptionQuery.data.activeSubscription &&
      subscriptionQuery.data.activeSubscription.cancelAtPeriodEnd === true &&
      subscriptionQuery.data.activeSubscription.trialType !== "admin" // if its an admin-trial then they cant cancel
    : true;

  const [isEditingOverage, setIsEditingOverage] = useState(false);

  const isLoading =
    subscriptionQuery.isPending ||
    (creditsQuery.isEnabled && creditsQuery.isPending);
  const isError = subscriptionQuery.isError || creditsQuery.isError;
  const isSuccess = subscriptionQuery.isSuccess && creditsQuery.isSuccess;
  const { isPermitted } = usePermissions();

  // Check permissions for the scheduled future subscription plan
  const nextSubPermissionKeys = subscriptionQuery.data?.activeSubscription
    ?.nextSub
    ? [subscriptionQuery.data.activeSubscription.nextSub.plan.product_key]
    : undefined;

  const { isPermitted: nextSubIsPermitted } = usePermissions({
    permissionSets: nextSubPermissionKeys,
  });

  const queryClient = useQueryClient();

  const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
  const [undoCancelModalIsOpen, setUndoCancelModalIsOpen] = useState(false);
  const [cancelPlanChangeModalIsOpen, setCancelPlanChangeModalIsOpen] =
    useState(false);

  const hasNoSubscription =
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data?.activeSubscription == null;

  const getGradientColor = (value: number) => {
    const red = Math.round((value / 100) * 255);
    const blue = 255 - red;
    return `rgb(${red}, 0, ${blue})`; // Gradient from blue (0%) to red (100%)
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        {/* Block for 'pending cancellation' alert */}
        {subscriptionQuery.isSuccess &&
          isSuccess &&
          subscriptionQuery.data.activeSubscription && (
            <>
              <ConfirmationModal
                isLoading={scheduleCancellationMutation.isPending}
                isError={scheduleCancellationMutation.isError}
                open={cancelModalIsOpen}
                header={`Confirm Cancellation`}
                body={
                  <>
                    You're about to cancel your{" "}
                    <strong>
                      {
                        subscriptionQuery.data.activeSubscription.plan
                          .display_name
                      }
                    </strong>{" "}
                    subscription. Your access will continue until the end of
                    your current billing term on{" "}
                    <strong>
                      {formatTimestamp(
                        subscriptionQuery.data.activeSubscription.renewalDate *
                          1000,
                      )}
                    </strong>
                    . After this date, your cancellation will be complete, and
                    all members who are not the owner will lose access to the
                    Hub at the end of the current term.
                  </>
                }
                leftButtonText="Go Back"
                leftButtonColor="primary"
                rightButtonText="Cancel Subscription"
                rightButtonVariant="contained"
                rightButtonColor="error"
                handleRightButton={() => scheduleCancellationMutation.mutate()}
                handleLeftButton={() => {
                  setCancelModalIsOpen(false);
                }}
                handleClose={() => setCancelModalIsOpen(false)}
              />
            </>
          )}

        {subscriptionQuery.isSuccess &&
          isSuccess &&
          subscriptionQuery.data.activeSubscription &&
          hasScheduledCancellation && (
            <>
              <Alert
                severity="info"
                variant="outlined"
                action={
                  <PermissionTooltipWrapper
                    permissionKeys={[PermissionsKeys.subscriptionUpdate]}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        undoScheduledCancellationMutation.reset();
                        setUndoCancelModalIsOpen(true);
                      }}
                    >
                      Cancel scheduled cancellation
                    </Button>
                  </PermissionTooltipWrapper>
                }
              >
                <AlertTitle>Scheduled plan cancellation</AlertTitle>
                You have a scheduled cancellation to your{" "}
                {
                  subscriptionQuery.data.activeSubscription.plan.display_name
                }{" "}
                subscription, that will take effect on the{" "}
                {formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000,
                )}
                . Until then you are still able to use all of the features of{" "}
                {toTitleCase(
                  subscriptionQuery.data.activeSubscription.plan
                    .product_display_name,
                )}
                .
              </Alert>
              <br></br>
              <Alert severity="warning" variant="outlined">
                All members who are not the owner will have their access to hub
                disabled at the end of the current subscription term{" "}
                {formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000,
                )}
                .
              </Alert>

              <ConfirmationModal
                isLoading={undoScheduledCancellationMutation.isPending}
                isError={undoScheduledCancellationMutation.isError}
                open={undoCancelModalIsOpen}
                header={`Keep Your Subscription Active?`}
                body={`You previously requested to cancel your subscription. By confirming here, you are cancelling that cancellation request. This means your ${
                  subscriptionQuery.data.activeSubscription.plan.display_name
                } subscription will continue, and renew on ${formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000,
                )}`}
                leftButtonText={"CANCEL"}
                leftButtonColor="primary"
                rightButtonText={"CONFIRM"}
                rightButtonColor="success"
                handleLeftButton={() => {
                  setUndoCancelModalIsOpen(false);
                }}
                handleRightButton={() =>
                  undoScheduledCancellationMutation.mutate()
                }
                handleClose={() => setUndoCancelModalIsOpen(false)}
              />
            </>
          )}

        {/* Block for 'scheduled downgrade' alert */}
        {subscriptionQuery.isSuccess &&
          isSuccess &&
          subscriptionQuery.data.activeSubscription &&
          hasScheduledUpgradeOrDowngrade && (
            <>
              <Alert
                severity="info"
                variant="outlined"
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      futureSubscriptionMutation.reset();
                      setCancelPlanChangeModalIsOpen(true);
                    }}
                  >
                    Cancel scheduled change
                  </Button>
                }
              >
                <AlertTitle>Scheduled plan change</AlertTitle>
                You have a scheduled plan change from{" "}
                {
                  subscriptionQuery.data.activeSubscription.plan.display_name
                } to{" "}
                {
                  subscriptionQuery.data.activeSubscription.nextSub!.plan
                    .display_name
                }{" "}
                that will take effect on the{" "}
                {formatTimestamp(
                  subscriptionQuery.data.activeSubscription.nextSub!.startTime *
                    1000,
                )}
                . Until then you are still able to use all of the features of{" "}
                {toTitleCase(
                  subscriptionQuery.data.activeSubscription.plan
                    .product_display_name,
                )}
                .
              </Alert>
              <br></br>
              {subscriptionQuery.data.activeSubscription.plan.tier >
                subscriptionQuery.data.activeSubscription.nextSub!.plan.tier &&
                !nextSubIsPermitted([PermissionsKeys.membersUpdate]) && (
                  <Alert severity="warning" variant="outlined">
                    <strong>WARNING,</strong> All members who are not the{" "}
                    <strong>owner</strong> will have their access to Hub
                    disabled at the end of the current subscription term on{" "}
                    {formatTimestamp(
                      subscriptionQuery.data.activeSubscription.nextSub!
                        .startTime * 1000,
                    )}
                    .
                  </Alert>
                )}

              <ConfirmationModal
                isLoading={futureSubscriptionMutation.isPending}
                isError={futureSubscriptionMutation.isError}
                open={cancelPlanChangeModalIsOpen}
                header={`Cancel scheduled plan change?`}
                body={`Are you sure you want to cancel the scheduled plan change? When cancelled, your previous subscription of ${
                  subscriptionQuery.data.activeSubscription.plan.display_name
                } will renew on ${formatTimestamp(
                  subscriptionQuery.data.activeSubscription.renewalDate * 1000,
                )}.`}
                leftButtonText={"GO BACK"}
                leftButtonColor="primary"
                rightButtonText={"CANCEL PLAN CHANGE"}
                rightButtonColor="success"
                handleLeftButton={() => {
                  setCancelPlanChangeModalIsOpen(false);
                }}
                handleRightButton={() => futureSubscriptionMutation.mutate()}
                handleClose={() => setCancelPlanChangeModalIsOpen(false)}
              />
            </>
          )}
      </Grid>

      <Grid item xs={12} md={6}>
        <CardHeader title="Current Plan" />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* Current Plan Card */}
        <Card>
          {isError && !hasNoSubscription && (
            <CardContent>
              <Alert variant="outlined" severity="error">
                An error occurred while fetching the subscription information.
              </Alert>
            </CardContent>
          )}
          {isLoading && (
            <CardContent>
              <Skeleton variant="rectangular" height="200px" />
            </CardContent>
          )}
          {!isLoading && hasNoSubscription && (
            <>
              <CardHeader title="None" />
              <CardContent>
                <Alert variant="outlined" severity="warning">
                  Hub is better with a sub, subscribe to get more out of Hub.
                </Alert>
              </CardContent>
            </>
          )}
          {isSuccess &&
            subscriptionQuery.data.activeSubscription &&
            creditsQuery.data && (
              <>
                <CardHeader
                  title={
                    toTitleCase(
                      subscriptionQuery.data.activeSubscription.plan
                        .product_display_name,
                    ) +
                    (subscriptionQuery.data.activeSubscription.trialType
                      ? " (Trial)"
                      : "")
                  }
                />
                <CardContent>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <InputLabel>Term</InputLabel>
                      <Typography variant="body2">
                        {formatInterval(
                          subscriptionQuery.data.activeSubscription.plan
                            .cadence,
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <InputLabel>Renewal Date</InputLabel>
                      <Typography variant="body2">
                        {formatTimestamp(
                          subscriptionQuery.data.activeSubscription
                            .renewalDate * 1000,
                        )}
                      </Typography>
                    </Box>
                    <Box>
                      <InputLabel>Credit Usage</InputLabel>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress
                          variant="determinate"
                          sx={{
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: getGradientColor(
                                Math.min(
                                  (creditsQuery.data.subCreditsSpent /
                                    creditsQuery.data.subCreditsLimit) *
                                    100,
                                  100,
                                ),
                              ),
                            },
                            width: "100%",
                          }}
                          value={Math.min(
                            (creditsQuery.data.subCreditsSpent /
                              creditsQuery.data.subCreditsLimit) *
                              100,
                            100,
                          )}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {`${creditsQuery.data.subCreditsSpent.toLocaleString()}/${creditsQuery.data.subCreditsLimit.toLocaleString()}`}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box>
                      <InputLabel>Support Add-ons:</InputLabel>
                      <Typography variant="body2">-</Typography>
                    </Box>
                    {isPermitted([PermissionsKeys.overageUpdate]) &&
                      !creditsQuery.data.isOverageEnabled && (
                        <Alert severity="warning" variant="outlined">
                          When you run out of credit, your apps will stop!
                        </Alert>
                      )}
                    <PermissionsWrapper
                      keys={[PermissionsKeys.subscriptionUpdate]}
                      messages={{
                        [PermissionsReasons.ROLE_DENY]: <></>,
                        [PermissionsReasons.EXTERNALLY_MANAGED]: (
                          <Alert variant="outlined" severity="info">
                            To manage your plan please contact support
                          </Alert>
                        ),
                      }}
                    >
                      {subscriptionQuery.data.activeSubscription.trialType ==
                        "user" && (
                        <Alert severity="warning" variant="outlined">
                          {/* This version of the message is for people who self-served a trial */}
                          Your{" "}
                          {
                            subscriptionQuery.data.activeSubscription.plan
                              .display_name
                          }{" "}
                          trial finishes on{" "}
                          {formatTimestamp(
                            subscriptionQuery.data.activeSubscription
                              .renewalDate * 1000,
                          )}
                          . Your subscription will automatically activate and
                          you'll be charged for the{" "}
                          {
                            subscriptionQuery.data.activeSubscription.plan
                              .display_name
                          }{" "}
                          on this date.
                        </Alert>
                      )}

                      {subscriptionQuery.data.activeSubscription.trialType ==
                        "admin" &&
                        !hasScheduledUpgradeOrDowngrade && ( // We dont need to warn them if they already queued a downgrade
                          <Alert severity="warning" variant="outlined">
                            {/* This version of the message is for people on an admin-initiated trial */}
                            Your{" "}
                            {
                              subscriptionQuery.data.activeSubscription.plan
                                .display_name
                            }{" "}
                            trial finishes on{" "}
                            {formatTimestamp(
                              subscriptionQuery.data.activeSubscription
                                .renewalDate * 1000,
                            )}
                            . Your subscription will automatically cancel on
                            this date.
                          </Alert>
                        )}
                    </PermissionsWrapper>
                  </Box>
                </CardContent>
              </>
            )}

          <CardActions
            sx={{
              justifyContent: "flex-end",
            }}
          >
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.subscriptionUpdate]}
              additionalDisabled={
                hasScheduledUpgradeOrDowngrade || hasScheduledCancellation
              }
              onClick={() => {
                history.push(Routes.orgManageSubscription);
              }}
            >
              MANAGE SUBSCRIPTION
            </PermissionTooltipButton>
          </CardActions>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}></Grid>
      <Grid item xs={12} md={6} justifyItems="right">
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant="outlined"
            onClick={() => setFeatureComparisonModalOpen(true)}
          >
            Feature Comparison
          </Button>
          <Button
            variant="outlined"
            onClick={() => setCreditUseModalOpen(true)}
          >
            Feature Costs
          </Button>
        </Stack>
      </Grid>

      <Grid item xs={12} md={6}>
        <CardHeader title="Overage" subheader="Set a maximum spend limit" />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* Overage card */}
        <Card>
          <CardHeader title="Overage Limit" />
          <PermissionsWrapper
            keys={[PermissionsKeys.overageRead]}
            permissionMessageTemplateFn={(
              content: string,
              action: React.ReactNode,
            ) => {
              return (
                <CardContent>
                  <Alert severity="warning" variant="outlined">
                    {content}
                  </Alert>
                </CardContent>
              );
            }}
          >
            <CardContent>
              {isError && (
                <Alert variant="outlined" severity="error">
                  An error occurred while fetching the overage information.
                </Alert>
              )}
              {isLoading && <Skeleton variant="rectangular" height="200px" />}
              {isSuccess &&
                subscriptionQuery.data.activeSubscription &&
                creditsQuery.data && (
                  <>
                    {paymentMethodQuery.data == null && (
                      <PermissionsWrapper
                        keys={[PermissionsKeys.paymentMethodUpdate]}
                        messages={{
                          [PermissionsReasons.ROLE_DENY]: (
                            <Alert
                              severity="warning"
                              variant="outlined"
                              style={{
                                marginTop: "32px",
                                marginBottom: "28px",
                              }}
                            >
                              Payment method must be configured to enable
                              overage.
                            </Alert>
                          ),
                        }}
                      >
                        <Alert
                          severity="warning"
                          variant="outlined"
                          style={{ marginTop: "32px", marginBottom: "28px" }}
                          action={
                            <Button
                              color="inherit"
                              onClick={() =>
                                history.push(Routes.orgBillingDetails)
                              }
                            >
                              Add Payment Method
                            </Button>
                          }
                        >
                          <AlertTitle>Overage can't be configured</AlertTitle>
                          Overage can't be configured without a valid payment
                          method attached to your account.
                        </Alert>
                      </PermissionsWrapper>
                    )}
                    {paymentMethodQuery.data !== null && (
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                          <InputLabel>Overage Rate</InputLabel>
                          {creditsQuery.data.isOverageEnabled && (
                            <Typography variant="body2">
                              {`$${creditsQuery.data.overageDollarsPerCreditsBlock} per ${creditsQuery.data.overageCreditsBlock} credits.`}
                            </Typography>
                          )}
                          {!creditsQuery.data.isOverageEnabled && (
                            <Typography variant="body2">-</Typography>
                          )}
                        </Box>
                        {!isEditingOverage && (
                          <>
                            <Box>
                              <InputLabel>Overage Spend</InputLabel>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    $
                                  </Typography>
                                </Box>
                                <Box sx={{ width: "100%", mr: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    sx={{
                                      "& .MuiLinearProgress-bar": {
                                        backgroundColor: getGradientColor(
                                          creditsQuery.data.isOverageEnabled
                                            ? Math.min(
                                                (creditsQuery.data
                                                  .overageDollarsSpent /
                                                  creditsQuery.data
                                                    .overageDollarsLimit) *
                                                  100,
                                                100,
                                              )
                                            : 0,
                                        ),
                                      },
                                    }}
                                    value={
                                      creditsQuery.data.isOverageEnabled
                                        ? Math.min(
                                            (creditsQuery.data
                                              .overageDollarsSpent /
                                              creditsQuery.data
                                                .overageDollarsLimit) *
                                              100,
                                            100,
                                          )
                                        : 0
                                    }
                                  />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {`${formatPrice(
                                      creditsQuery.data.overageDollarsSpent,
                                    )}/$${
                                      creditsQuery.data.overageDollarsLimit
                                    }`}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Typography variant="subtitle1">
                              {`You have a $${creditsQuery.data.overageDollarsLimit.toLocaleString()} overage limit, set on your organisation.`}
                            </Typography>
                            {creditsQuery.data.isOverageEnabled &&
                              creditsQuery.data.overageDollarsSpent >=
                                creditsQuery.data.overageDollarsLimit && (
                                <Alert severity="error" variant="outlined">
                                  You've consumed all your set overage up to
                                  your limit.
                                </Alert>
                              )}
                          </>
                        )}
                        {isEditingOverage && (
                          <OverageField
                            currentOverage={creditsQuery.data}
                            plan={
                              subscriptionQuery.data.activeSubscription.plan
                            }
                            onCancel={() => setIsEditingOverage(false)}
                            onSubmit={() => setIsEditingOverage(false)}
                          />
                        )}
                      </Box>
                    )}
                  </>
                )}
            </CardContent>
            {isSuccess &&
              !isEditingOverage &&
              paymentMethodQuery.data !== null &&
              subscriptionQuery.data.activeSubscription && (
                <CardActions
                  sx={{
                    justifyContent: "flex-end",
                  }}
                >
                  <PermissionTooltipButton
                    permissionKeys={[PermissionsKeys.overageUpdate]}
                    onClick={() => {
                      setIsEditingOverage(true);
                    }}
                  >
                    Edit
                  </PermissionTooltipButton>
                </CardActions>
              )}
          </PermissionsWrapper>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <CardHeader title="Cancel Subscription" />
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Need to take a break?" />

          <CardContent>
            <Typography variant="body1">
              Cancel your subscription at any time, you’ll lose access to hub
              features at the end of your current subscription term.
            </Typography>
          </CardContent>
          <CardActions
            sx={{
              justifyContent: "flex-end",
            }}
          >
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.subscriptionDelete]}
              variant="destructive"
              additionalDisabled={
                hasScheduledUpgradeOrDowngrade || hasScheduledCancellation
              }
              onClick={() => setCancelModalIsOpen(true)}
            >
              Cancel subscription
            </PermissionTooltipButton>
          </CardActions>
        </Card>
      </Grid>
      <FeatureComparisonModal
        open={featureComparisonModalOpen}
        onClose={() => setFeatureComparisonModalOpen(false)}
      />
      <CreditUseModal
        open={creditUseModalOpen}
        onClose={() => setCreditUseModalOpen(false)}
      />
    </Grid>
  );
};
